package co.laiautomation.downtyme

import com.laiautomation.kdxp.appmodel.KdxpAppSnapshot
import com.laiautomation.kdxp.appmodel.KdxpBackAction
import com.laiautomation.kdxp.appmodel.KdxpDataSourceDefinition
import com.laiautomation.kdxp.appmodel.KdxpNavigationGraph
import com.laiautomation.kdxp.appmodel.KdxpNavigationIcon
import com.laiautomation.kdxp.appmodel.KdxpNavigationItemDefinition
import com.laiautomation.kdxp.appmodel.KdxpNavigationPlacement
import com.laiautomation.kdxp.appmodel.KdxpNavigateAction
import com.laiautomation.kdxp.appmodel.KdxpObjectDataSource
import com.laiautomation.kdxp.appmodel.KdxpRefreshDataAction
import com.laiautomation.kdxp.appmodel.KdxpRouteDefinition
import com.laiautomation.kdxp.appmodel.KdxpSchemaDataSource
import com.laiautomation.kdxp.appmodel.KdxpScreenDefinition
import com.laiautomation.kdxp.appmodel.KdxpThemeDefinition
import com.laiautomation.kdxp.appmodel.KdxpWidgetNode
import com.laiautomation.kdxp.ui.widgets.data.KdxpCollectionWidgetTypes
import com.laiautomation.kdxp.ui.widgets.data.KdxpRecordWidgetTypes
import com.laiautomation.kdxp.ui.widgets.forms.KdxpFormWidgetTypes
import com.laiautomation.kdxp.widgetruntime.KdxpWidgetTypes
import kotlinx.serialization.json.JsonPrimitive

/**
 * Builds Downtyme's default campaign application from stock KDXP widgets.
 *
 * The snapshot intentionally contains no custom composables. Applications can
 * later replace or rearrange these screens through KDXP configuration without
 * changing Downtyme's data contracts.
 */
fun downtymeAppSnapshot(
    appKey: String,
    displayName: String,
): KdxpAppSnapshot {
    val sections = listOf(
        DowntymeSection(
            id = "characters",
            title = "Characters",
            singular = "Character",
            iconKey = "users",
            modelKey = DowntymeObjectKeys.CHARACTER,
            restContextPath = DowntymeObjectKeys.CHARACTER_REST_CONTEXT,
            layout = "visual-list",
            visualContent = true,
            emptyText = "No characters have been created for this campaign.",
        ),
        DowntymeSection(
            id = "areas",
            title = "Areas",
            singular = "Area",
            iconKey = "map",
            modelKey = DowntymeObjectKeys.AREA,
            restContextPath = DowntymeObjectKeys.AREA_REST_CONTEXT,
            layout = "visual-list",
            visualContent = true,
            emptyText = "No campaign areas have been created yet.",
        ),
        DowntymeSection(
            id = "actions",
            title = "Downtime Actions",
            singular = "Downtime Action",
            iconKey = "list-checks",
            modelKey = DowntymeObjectKeys.ACTION,
            restContextPath = DowntymeObjectKeys.ACTION_REST_CONTEXT,
            layout = "list",
            emptyText = "No downtime actions are available yet.",
        ),
        DowntymeSection(
            id = "resources",
            title = "Currencies & Resources",
            singular = "Currency or Resource",
            iconKey = "coins",
            modelKey = DowntymeObjectKeys.RESOURCE,
            restContextPath = DowntymeObjectKeys.RESOURCE_REST_CONTEXT,
            layout = "list",
            emptyText = "No currencies or resources have been configured.",
        ),
    )

    val dashboard = dashboardScreen(displayName, sections)
    val sectionScreens = sections.flatMap(::sectionScreens)
    val historyScreen = actionHistoryScreen()

    return KdxpAppSnapshot(
        appKey = appKey,
        displayName = displayName,
        navigation = KdxpNavigationGraph(
            startRoute = "campaign-home",
            routes = buildList {
                add(
                    KdxpRouteDefinition(
                        id = "campaign-home",
                        path = "/",
                        screenId = dashboard.id,
                        navigationItem = navItem("Campaign", "house", 0),
                    ),
                )
                sections.forEachIndexed { index, section ->
                    add(
                        KdxpRouteDefinition(
                            id = section.routeId,
                            path = "/${section.id}",
                            screenId = section.screenId,
                            navigationItem = navItem(
                                section.title,
                                section.iconKey,
                                (index + 1) * 10,
                            ),
                        ),
                    )
                    if (section.visualContent) {
                        add(
                            KdxpRouteDefinition(
                                id = section.detailRouteId,
                                path = "/${section.id}/detail",
                                screenId = section.detailScreenId,
                            ),
                        )
                        add(
                            KdxpRouteDefinition(
                                id = section.editRouteId,
                                path = "/${section.id}/edit",
                                screenId = section.editScreenId,
                            ),
                        )
                    }
                }
                add(
                    KdxpRouteDefinition(
                        id = "action-history",
                        path = "/history",
                        screenId = historyScreen.id,
                        navigationItem = navItem("History", "history", 60),
                    ),
                )
            },
        ),
        screens = listOf(dashboard) + sectionScreens + historyScreen,
        dataSources = sections.flatMap(::sectionDataSources) + listOf(
            KdxpObjectDataSource(
                id = "action-logs-data",
                restContextPath = DowntymeObjectKeys.ACTION_LOG_REST_CONTEXT,
                schemaDataSourceId = "action-logs-schema",
                sort = "dateCreated:desc",
                pageSize = 20,
            ),
            KdxpSchemaDataSource(
                id = "action-logs-schema",
                modelKey = DowntymeObjectKeys.ACTION_LOG,
            ),
        ),
        theme = KdxpThemeDefinition(
            spacing = mapOf(
                "screen" to 16.0,
                "section" to 12.0,
            ),
            shapes = mapOf(
                "card" to 12.0,
            ),
        ),
        metadata = mapOf(
            "product" to "downtyme",
            "rulesetKey" to "generic",
        ),
    )
}

private data class DowntymeSection(
    val id: String,
    val title: String,
    val singular: String,
    val iconKey: String,
    val modelKey: String,
    val restContextPath: String,
    val layout: String,
    val visualContent: Boolean = false,
    val emptyText: String,
) {
    val routeId: String get() = "campaign-$id"
    val screenId: String get() = "$id-screen"
    val dataSourceId: String get() = "$id-data"
    val schemaSourceId: String get() = "$id-schema"
    val selectionPath: String get() = "selection.$id"
    val detailRouteId: String get() = "$routeId-detail"
    val editRouteId: String get() = "$routeId-edit"
    val detailScreenId: String get() = "$id-detail-screen"
    val editScreenId: String get() = "$id-edit-screen"
}

private fun dashboardScreen(
    displayName: String,
    sections: List<DowntymeSection>,
): KdxpScreenDefinition = KdxpScreenDefinition(
    id = "campaign-dashboard-screen",
    title = "Campaign",
    root = screenRoot(
        id = "campaign-dashboard",
        children = listOf(
            textNode(
                id = "campaign-title",
                text = displayName,
                style = "headlineMedium",
                bold = true,
            ),
            textNode(
                id = "campaign-subtitle",
                text = "Manage the people, places, actions, and resources that shape this campaign.",
                style = "bodyLarge",
            ),
            KdxpWidgetNode(
                id = "campaign-dashboard-actions",
                widgetType = KdxpWidgetTypes.COLUMN,
                properties = mapOf(
                    "fillWidth" to JsonPrimitive(true),
                    "spacing" to JsonPrimitive(10),
                ),
                children = sections.map { section ->
                    navigationButton(
                        id = "open-${section.id}",
                        label = section.title,
                        routeId = section.routeId,
                    )
                } + navigationButton(
                    id = "open-action-history",
                    label = "Action History",
                    routeId = "action-history",
                ),
            ),
        ),
    ),
)

private fun sectionScreens(section: DowntymeSection): List<KdxpScreenDefinition> =
    if (section.visualContent) {
        listOf(
            visualCollectionScreen(section),
            visualDetailScreen(section),
            visualEditScreen(section),
        )
    } else {
        listOf(conventionalSectionScreen(section))
    }

private fun visualCollectionScreen(section: DowntymeSection): KdxpScreenDefinition = KdxpScreenDefinition(
    id = section.screenId,
    title = section.title,
    root = screenRoot(
        id = "${section.id}-root",
        children = listOf(
            KdxpWidgetNode(
                id = "${section.id}-collection",
                widgetType = KdxpCollectionWidgetTypes.COLLECTION,
                properties = mapOf(
                    "dataSourceId" to JsonPrimitive(section.dataSourceId),
                    "itemsPath" to JsonPrimitive("items"),
                    "layout" to JsonPrimitive("visual-list"),
                    "titleField" to JsonPrimitive("name"),
                    "subtitleField" to JsonPrimitive("description"),
                    "imageField" to JsonPrimitive("imageUrl"),
                    "searchEnabled" to JsonPrimitive(true),
                    "searchLabel" to JsonPrimitive("Search ${section.title.lowercase()}"),
                    "showRefresh" to JsonPrimitive(true),
                    "pageSize" to JsonPrimitive(12),
                    "selectedRecordStatePath" to JsonPrimitive(section.selectionPath),
                    "emptyText" to JsonPrimitive(section.emptyText),
                    "showCreate" to JsonPrimitive(true),
                    "createPresentation" to JsonPrimitive("drawer"),
                    "createContentDescription" to JsonPrimitive("Create ${section.singular.lowercase()}"),
                    "quickCreateSchemaDataSourceId" to JsonPrimitive(section.schemaSourceId),
                    "quickCreateModelKey" to JsonPrimitive(section.modelKey),
                    "quickCreateFields" to JsonPrimitive("imageUrl,name"),
                    "quickCreateStatePath" to JsonPrimitive("forms.${section.id}.quickCreate"),
                    "fillWidth" to JsonPrimitive(true),
                ),
                actions = mapOf(
                    "onRefresh" to listOf(KdxpRefreshDataAction(section.dataSourceId)),
                    "onItemClick" to listOf(KdxpNavigateAction(routeId = section.detailRouteId)),
                ),
            ),
        ),
    ),
)

private fun visualDetailScreen(section: DowntymeSection): KdxpScreenDefinition = KdxpScreenDefinition(
    id = section.detailScreenId,
    title = section.singular,
    root = screenRoot(
        id = "${section.id}-detail-root",
        children = listOf(
            KdxpWidgetNode(
                id = "${section.id}-detail",
                widgetType = KdxpRecordWidgetTypes.RECORD_DETAIL,
                properties = buildMap {
                    put("schemaDataSourceId", JsonPrimitive(section.schemaSourceId))
                    put("recordStatePath", JsonPrimitive(section.selectionPath))
                    put("presentation", JsonPrimitive("content"))
                    put("showTitle", JsonPrimitive(false))
                    put("titleField", JsonPrimitive("name"))
                    put("heroImageField", JsonPrimitive("imageUrl"))
                    put("badgeText", JsonPrimitive(section.singular))
                    put("showEditPencil", JsonPrimitive(true))
                    put("excludedFields", JsonPrimitive("id,externalReferenceCode,creator,dateCreated,dateModified,status"))
                    put("showEmptyValues", JsonPrimitive(false))
                    put("directEdit", JsonPrimitive(true))
                    put("metadataFields", JsonPrimitive(section.contentMetadataFields()))
                    put("detailSections", JsonPrimitive(section.contentDetailSections()))
                    put("emptyText", JsonPrimitive("Select a ${section.singular.lowercase()} to view details."))
                    put("fillWidth", JsonPrimitive(true))
                },
                actions = mapOf(
                    "onEdit" to listOf(KdxpNavigateAction(routeId = section.editRouteId)),
                    "onFieldEdit" to listOf(KdxpNavigateAction(routeId = section.editRouteId)),
                ),
            ),
        ),
    ),
)

private fun DowntymeSection.contentMetadataFields(): String = when (id) {
    "characters" -> detailMetadata(
        field = "capital",
        label = "Capital",
        icon = "coins",
        emptyText = "0",
    )
    else -> ""
}

private fun DowntymeSection.contentDetailSections(): String = when (id) {
    "characters" -> listOf(
        detailSection(
            field = "description",
            title = "Description",
            icon = "feather",
            emptyTitle = "No description yet",
            emptyMessage = "Add a description to bring this character to life.",
        ),
        detailSection(
            field = "areaCharacters",
            title = "Connections",
            icon = "link",
            emptyTitle = "No connected areas yet",
            emptyMessage = "Connect this character to an area to place them in the campaign world.",
        ),
    ).joinToString(";;")
    "areas" -> detailSection(
        field = "description",
        title = "Description",
        icon = "feather",
        emptyTitle = "No description yet",
        emptyMessage = "Add a description to bring this area to life.",
    )
    else -> ""
}

private fun detailMetadata(
    field: String,
    label: String,
    icon: String,
    emptyText: String = "—",
    hideWhenEmpty: Boolean = false,
    editable: Boolean = true,
): String = listOf(
    field,
    label,
    icon,
    emptyText,
    hideWhenEmpty.toString(),
    editable.toString(),
).joinToString("|")

private fun detailSection(
    field: String,
    title: String,
    icon: String,
    emptyTitle: String,
    emptyMessage: String,
    hideWhenEmpty: Boolean = false,
    editable: Boolean = true,
): String = listOf(
    field,
    title,
    icon,
    emptyTitle,
    emptyMessage,
    hideWhenEmpty.toString(),
    editable.toString(),
).joinToString("|")

private fun visualEditScreen(section: DowntymeSection): KdxpScreenDefinition = KdxpScreenDefinition(
    id = section.editScreenId,
    title = "Edit ${section.singular}",
    root = screenRoot(
        id = "${section.id}-edit-root",
        children = listOf(
            KdxpWidgetNode(
                id = "${section.id}-edit-form",
                widgetType = KdxpFormWidgetTypes.SCHEMA_FORM,
                properties = mapOf(
                    "schemaDataSourceId" to JsonPrimitive(section.schemaSourceId),
                    "modelKey" to JsonPrimitive(section.modelKey),
                    "statePath" to JsonPrimitive("forms.${section.id}.edit"),
                    "recordStatePath" to JsonPrimitive(section.selectionPath),
                    "resultStatePath" to JsonPrimitive(section.selectionPath),
                    "mode" to JsonPrimitive("edit"),
                    "title" to JsonPrimitive("Edit ${section.singular.lowercase()}"),
                    "submitLabel" to JsonPrimitive("Save ${section.singular.lowercase()}"),
                    "successMessage" to JsonPrimitive("${section.singular} saved."),
                    "showTitle" to JsonPrimitive(true),
                    "showCancel" to JsonPrimitive(true),
                    "showReadOnly" to JsonPrimitive(false),
                    "fillWidth" to JsonPrimitive(true),
                ),
                actions = mapOf(
                    "onSubmit" to listOf(
                        KdxpRefreshDataAction(section.dataSourceId),
                        KdxpBackAction,
                    ),
                    "onCancel" to listOf(KdxpBackAction),
                ),
            ),
        ),
    ),
)

private fun conventionalSectionScreen(section: DowntymeSection): KdxpScreenDefinition = KdxpScreenDefinition(
    id = section.screenId,
    title = section.title,
    root = screenRoot(
        id = "${section.id}-root",
        children = listOf(
            textNode(
                id = "${section.id}-title",
                text = section.title,
                style = "headlineMedium",
                bold = true,
            ),
            KdxpWidgetNode(
                id = "${section.id}-collection",
                widgetType = KdxpCollectionWidgetTypes.COLLECTION,
                properties = mapOf(
                    "dataSourceId" to JsonPrimitive(section.dataSourceId),
                    "itemsPath" to JsonPrimitive("items"),
                    "layout" to JsonPrimitive(section.layout),
                    "titleField" to JsonPrimitive("name"),
                    "subtitleField" to JsonPrimitive("description"),
                    "imageField" to JsonPrimitive("imageUrl"),
                    "searchEnabled" to JsonPrimitive(true),
                    "searchLabel" to JsonPrimitive("Search ${section.title.lowercase()}"),
                    "showRefresh" to JsonPrimitive(true),
                    "pageSize" to JsonPrimitive(12),
                    "columns" to JsonPrimitive(2),
                    "selectedRecordStatePath" to JsonPrimitive(section.selectionPath),
                    "emptyText" to JsonPrimitive(section.emptyText),
                    "fillWidth" to JsonPrimitive(true),
                ),
                actions = mapOf(
                    "onRefresh" to listOf(KdxpRefreshDataAction(section.dataSourceId)),
                ),
            ),
            KdxpWidgetNode(
                id = "${section.id}-detail",
                widgetType = KdxpRecordWidgetTypes.RECORD_DETAIL,
                properties = mapOf(
                    "schemaDataSourceId" to JsonPrimitive(section.schemaSourceId),
                    "recordStatePath" to JsonPrimitive(section.selectionPath),
                    "title" to JsonPrimitive("Selected ${section.singular}"),
                    "showTitle" to JsonPrimitive(true),
                    "titleField" to JsonPrimitive("name"),
                    "subtitleField" to JsonPrimitive("description"),
                    "excludedFields" to JsonPrimitive("id,externalReferenceCode,creator,dateCreated,dateModified,status"),
                    "showEmptyValues" to JsonPrimitive(false),
                    "emptyText" to JsonPrimitive("Select a ${section.singular.lowercase()} to view details."),
                    "fillWidth" to JsonPrimitive(true),
                ),
            ),
            KdxpWidgetNode(
                id = "${section.id}-form",
                widgetType = KdxpFormWidgetTypes.SCHEMA_FORM,
                properties = mapOf(
                    "schemaDataSourceId" to JsonPrimitive(section.schemaSourceId),
                    "modelKey" to JsonPrimitive(section.modelKey),
                    "statePath" to JsonPrimitive("forms.${section.id}"),
                    "recordStatePath" to JsonPrimitive(section.selectionPath),
                    "resultStatePath" to JsonPrimitive(section.selectionPath),
                    "mode" to JsonPrimitive("auto"),
                    "title" to JsonPrimitive("Create or edit ${section.singular.lowercase()}"),
                    "submitLabel" to JsonPrimitive("Save ${section.singular.lowercase()}"),
                    "successMessage" to JsonPrimitive("${section.singular} saved."),
                    "showTitle" to JsonPrimitive(true),
                    "showCancel" to JsonPrimitive(false),
                    "showReadOnly" to JsonPrimitive(false),
                    "fillWidth" to JsonPrimitive(true),
                ),
                actions = mapOf(
                    "onSubmit" to listOf(KdxpRefreshDataAction(section.dataSourceId)),
                ),
            ),
        ),
    ),
)

private fun actionHistoryScreen(): KdxpScreenDefinition = KdxpScreenDefinition(
    id = "action-history-screen",
    title = "Action History",
    root = screenRoot(
        id = "action-history-root",
        children = listOf(
            textNode(
                id = "action-history-title",
                text = "Action History",
                style = "headlineMedium",
                bold = true,
            ),
            textNode(
                id = "action-history-description",
                text = "A campaign-wide record of submitted and resolved downtime actions.",
                style = "bodyLarge",
            ),
            KdxpWidgetNode(
                id = "action-history-collection",
                widgetType = KdxpCollectionWidgetTypes.COLLECTION,
                properties = mapOf(
                    "dataSourceId" to JsonPrimitive("action-logs-data"),
                    "itemsPath" to JsonPrimitive("items"),
                    "layout" to JsonPrimitive("list"),
                    "titleField" to JsonPrimitive("notes"),
                    "subtitleField" to JsonPrimitive("amountSpent"),
                    "searchEnabled" to JsonPrimitive(true),
                    "searchLabel" to JsonPrimitive("Search action history"),
                    "showRefresh" to JsonPrimitive(true),
                    "pageSize" to JsonPrimitive(20),
                    "selectedRecordStatePath" to JsonPrimitive("selection.actionLog"),
                    "emptyText" to JsonPrimitive("No downtime actions have been logged yet."),
                    "fillWidth" to JsonPrimitive(true),
                ),
                actions = mapOf(
                    "onRefresh" to listOf(KdxpRefreshDataAction("action-logs-data")),
                ),
            ),
            KdxpWidgetNode(
                id = "action-history-detail",
                widgetType = KdxpRecordWidgetTypes.RECORD_DETAIL,
                properties = mapOf(
                    "schemaDataSourceId" to JsonPrimitive("action-logs-schema"),
                    "recordStatePath" to JsonPrimitive("selection.actionLog"),
                    "title" to JsonPrimitive("Action Log"),
                    "showTitle" to JsonPrimitive(true),
                    "excludedFields" to JsonPrimitive("id,externalReferenceCode,creator,status"),
                    "showEmptyValues" to JsonPrimitive(false),
                    "emptyText" to JsonPrimitive("Select an action log to view details."),
                    "fillWidth" to JsonPrimitive(true),
                ),
            ),
        ),
    ),
)

private fun sectionDataSources(section: DowntymeSection): List<KdxpDataSourceDefinition> = listOf(
    KdxpObjectDataSource(
        id = section.dataSourceId,
        restContextPath = section.restContextPath,
        schemaDataSourceId = section.schemaSourceId,
        sort = "name:asc",
        pageSize = 20,
    ),
    KdxpSchemaDataSource(
        id = section.schemaSourceId,
        modelKey = section.modelKey,
    ),
)

private fun screenRoot(
    id: String,
    children: List<KdxpWidgetNode>,
): KdxpWidgetNode = KdxpWidgetNode(
    id = id,
    widgetType = KdxpWidgetTypes.SCREEN,
    properties = mapOf(
        "padding" to JsonPrimitive(16),
        "scrollable" to JsonPrimitive(true),
    ),
    children = listOf(
        KdxpWidgetNode(
            id = "$id-column",
            widgetType = KdxpWidgetTypes.COLUMN,
            properties = mapOf(
                "fillWidth" to JsonPrimitive(true),
                "spacing" to JsonPrimitive(16),
            ),
            children = children,
        ),
    ),
)

private fun textNode(
    id: String,
    text: String,
    style: String,
    bold: Boolean = false,
): KdxpWidgetNode = KdxpWidgetNode(
    id = id,
    widgetType = KdxpWidgetTypes.TEXT,
    properties = buildMap {
        put("text", JsonPrimitive(text))
        put("style", JsonPrimitive(style))
        if (bold) put("fontWeight", JsonPrimitive("bold"))
    },
)

private fun navigationButton(
    id: String,
    label: String,
    routeId: String,
): KdxpWidgetNode = KdxpWidgetNode(
    id = id,
    widgetType = KdxpWidgetTypes.BUTTON,
    properties = mapOf(
        "text" to JsonPrimitive(label),
        "fillWidth" to JsonPrimitive(true),
    ),
    actions = mapOf(
        "onClick" to listOf(KdxpNavigateAction(routeId = routeId)),
    ),
)

private fun navItem(
    label: String,
    iconKey: String,
    order: Int,
): KdxpNavigationItemDefinition = KdxpNavigationItemDefinition(
    label = label,
    icon = KdxpNavigationIcon(lucideKey = iconKey),
    placement = KdxpNavigationPlacement.PRIMARY,
    order = order,
)
